// Added by Sejune Oh on 2020-03-24
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState, useEffect, useRef } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'
import cn from 'classnames'

// Helper
import Intl from '@/helper/Intl'
import { parseLogs } from '@/helper/util'

// UI
import { DialogWrapper, DialogWrapperProps } from '../DialogWrapper'
import { Button } from '@/components/worklist/Button'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface TaskLogDialogProps extends DialogWrapperProps {
  /** Log infos */
  taskLogsInfo?: TaskLogsInfoProps | null
  /** Download */
  downloadFile?: () => void
  /** Download button text */
  btnTextDownload?: string
  /** OK button text */
  btnTextOk?: string
  /** WC용 Styles  */
  classes?: any
}

export interface TaskLogsInfoProps {
  keys: string[]
  logs: {
    [key: string]: {
      /** Log messages */
      messages: string[]
      /** Filename */
      fileName?: string
    }
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskMessageDialog: FC<TaskLogDialogProps> = ({
  taskLogsInfo,
  open,
  title: titleParam,
  onCloseBtnClick,
  downloadFile,
  btnTextDownload: btnTextDownloadParam,
  btnTextOk: btnTextOkParam,
  classes: webComponentClassses,
  ...props
}) => {
  const classes = webComponentClassses
    ? webComponentClassses['tasklogdialog']
    : makeStyles(() => createStyles({ ...customStyles }))()
  const logViewRef = useRef<HTMLDivElement>(null)

  const title = titleParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklogdialog.title' })
  const btnTextDownload =
    btnTextDownloadParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklogdialog.btn_download' })
  const btnTextOk = btnTextOkParam || Intl.getIntlText({ id: 'htmljs_react.worklist.tasklogdialog.btn_ok' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! State
  const [taskLogtype, setTaskLogType] = useState<string>('')

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Evt Handler
  const handleDownload = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!downloadFile) return

    downloadFile()
  }

  const handleChangeLogTypeBtnClick = (type: string) => {
    setTaskLogType(type)
  }

  const getDialogWidth = () => {
    const innerWidth = window.innerWidth * 0.8
    return 1280 > innerWidth ? innerWidth : 1280
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effect
  useEffect(() => {
      setTaskLogType(taskLogsInfo ? taskLogsInfo.keys[0] : '')
  }, [taskLogsInfo])
  useEffect(() => {
    let mounted = true

    setTimeout(() => {
      if (mounted && logViewRef.current) {
        logViewRef.current!.scrollTop = logViewRef.current!.scrollHeight
      }
    }, 300)

    return () => {
      mounted = false
    }
  }, [taskLogtype, open])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <DialogWrapper
      className={webComponentClassses}
      open={open}
      title={title}
      onCloseBtnClick={onCloseBtnClick}
      minwidth={getDialogWidth()}
      maxWidth={'lg'}
    >
      <>
        <div className={classes.logviewWrapper}>
          <div className={classes.logView} ref={logViewRef}>
            {(taskLogsInfo && taskLogsInfo.logs[taskLogtype]) &&
              taskLogsInfo.logs[taskLogtype].messages.map((line, index) => {
                if (!line) return null

                const parsedLine = parseLogs(line)
                if (!parsedLine) return null

                const { date, time, pid, logLevel: _logLevel, message } = parsedLine
                const logLevel = _logLevel ? _logLevel.toLocaleLowerCase() : 'general'
                return (
                  <p key={`log-line-${index}`}>
                    {date && (
                      <span data-type={'time'}>
                        {date}
                        {time ? ' ' + time : ''}
                      </span>
                    )}
                    {pid && <span data-type={'pid'}>{pid}</span>}
                    {_logLevel && <span data-type={logLevel}>{_logLevel}</span>}
                    <span data-type={'msg' + logLevel} dangerouslySetInnerHTML={{ __html: message || '' }}></span>
                  </p>
                )
              })}
          </div>
        </div>
        <div className={classes.ctrWrapper}>
          <div className={classes.ctrRow}>
            <Button classes={webComponentClassses} disabled={!downloadFile} onBtnClick={handleDownload}>
              {btnTextDownload}
            </Button>
            <div className={classes.ctrToggle}>
              {taskLogsInfo &&
                taskLogsInfo.keys.map((key) => (
                  <div
                    key={`log-type-${key}`}
                    data-type="first"
                    className={cn({ [classes.activeToggle]: taskLogtype === key })}
                    onClick={() => handleChangeLogTypeBtnClick(key)}
                  >
                    {key}
                  </div>
                ))}
            </div>
          </div>
          <div className={classes.ctrRow}>
            <Button classes={webComponentClassses} disabled={false} onBtnClick={onCloseBtnClick}>
              {btnTextOk}
            </Button>
          </div>
        </div>
      </>
    </DialogWrapper>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Style
export const customStyles: Record<string, any> = {
  logviewWrapper: {
    padding: '8px',
  },
  logView: {
    height: '80vh',
    overflowY: 'auto',
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    padding: '8px',
    boxSizing: 'border-box',
    background: '#1e1e1e',
    lineHeight: 1.2,
    '&::-webkit-scrollbar-thumb': {
      width: '50px',
      height: '50px',
      borderRadius: '4px',
      background: '#646464',
    },
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '& > p': {
      color: '#fff',
      fontSize: '12px',
      margin: 0,
      lineHeight: 1.5,
      '& [data-type=time]': {
        color: '#64b244',
        marginRight: '8px',
      },
      '& [data-type=pid]': {
        color: '#237FE8',
        marginRight: '8px',
      },
      '& [data-type=info]': {
        color: '#5f8ad3',
        marginRight: '8px',
      },
      '& [data-type=warn]': {
        color: '#FFAE00',
        marginRight: '8px',
      },
      '& [data-type=erro]': {
        color: '#FB135A',
        marginRight: '8px',
      },
      // TRACE
      '& [data-type=trac]': {
        marginRight: '8px',
      },
      // DEBUG
      '& [data-type=debg]': {
        marginRight: '8px',
      },
      // FATAL
      '& [data-type=fatl]': {
        color: '#FF0000',
        marginRight: '8px',
      },
      '& [data-type=msgwarn]': {
        color: '#FFBE4D',
      },
      '& [data-type=msgerro]': {
        color: '#f44747',
      },
    },
  },
  ctrWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 8px 8px 8px',
  },
  ctrRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    color: '#e6eaec',
    position: 'relative',
    '&:first-of-type': {
      justifyContent: 'space-between',
      paddingBottom: '16px',
    },
  },
  ctrToggle: {
    display: 'flex',
    position: 'absolute',
    left: 0,
    right: 0,
    margin: 'auto',
    justifyContent: 'center',
    width: 'fit-content',
    '&> div': {
      cursor: 'pointer',
      height: '30px',
      padding: '0 12px',
      fontSize: '11px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#a5a5a5',
      backgroundColor: '#272b2e',
      border: '1px solid #242424',
      '&:first-of-type': {
        borderRight: '0',
      },
    },
  },
  activeToggle: {
    color: '#53b8ff !important',
    backgroundColor: '#1a1a1a !important',
  },
}

export default TaskMessageDialog
